/**
 * App shell — the app registry: every app the new sidebar (1b) can show, and who sees it.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * Plan: the app shell plan (2026-10-08) §2 — resume block in Documents/Admin-data-table-redesign.md §0
 *
 *   NAV_APPS           one row per sidebar app, in the design's default order (DEF)
 *   HELP_LINK          the fixed "Teamio how-to" row at the bottom (external, never reordered)
 *   FOLLOWUP_SUBJECTS  the four follow-up lists — a copy of pageChrome.FOLLOWUP_TABS (key, subject,
 *                      route name), agreement-tested, plus each list's path and icon
 *   PAGE_ALIASES       extra words the search box matches a page on (design ALIAS)
 *   visibleApps        the rows this user may see, with the Follow-ups target resolved
 *   firstFollowupRoute the first follow-up list this user may open
 *   activeAppId        which app a route path belongs to (longest prefix)
 *
 * WHO SEES WHAT — copied from the CLASSIC sidebar (src/components/Sidebar/Sidebar.vue), never
 * from the design: a row is shown exactly when the classic sidebar shows the same link.
 * cypress/node-tests/nav-apps-registry.test.js scans Sidebar.vue and fails if the two disagree.
 *
 *   'can:<subject>'  $can('view', subject)            (the classic v-if="$can('view', '<subject>')")
 *   'flag:<name>'    flags[name] === true, computed by the Vue layer (the classic computed v-if)
 *   'always'         no v-if in the classic sidebar
 *   'followups'      synthetic — the four lists live in the classic HEADER, not the sidebar.
 *                    Shown when any list is visible (pageChrome.visibleTabs' rule:
 *                    $can('view', subject) || $can('view', 'all')); opens the first visible list.
 *
 * Labels, short labels (tablet rail), descriptions and icon keys come from the design export
 * (APPS / SHORT). `icon` is the design's icon key, drawn by the AppShell components.
 * `dot` names an unread-dot source and `countKey` a count-pill source; the Vue layer maps them.
 */

/** The design's ’ (U+2019) is kept in labels so the page text matches the design. */
export const NAV_APPS = [
  { id: 'pulse', label: 'Practice Ops', shortLabel: 'Ops', icon: 'gauge', description: 'Live patient flow', to: { path: '/app/practice-pulse' }, rule: 'can:practice-pulse' },
  { id: 'today', label: 'Today', shortLabel: 'Today', icon: 'home', description: 'Your follow-ups and tasks for the day', to: { path: '/app/admin' }, rule: 'always' },
  { id: 'followups', label: 'Follow-ups', shortLabel: 'Follow-ups', icon: 'phoneOut', description: 'Recall, unscheduled tx, aging, broken appts', to: null, rule: 'followups' },
  { id: 'schedule', label: 'Schedule - Chart Audit', shortLabel: 'Chart Audit', icon: 'cal', description: 'The day by operatory, with a chart audit', to: { path: '/app/schedule' }, rule: 'can:schedule' },
  { id: 'reminders', label: 'Appointment reminders', shortLabel: 'Reminders', icon: 'calCheck', description: 'Confirmations and reminder texts', to: { path: '/app/apt-reminder' }, rule: 'can:apt-reminder' },
  { id: 'huddle', label: 'Today\u2019s huddle', shortLabel: 'Huddle', icon: 'users', description: 'Morning huddle sheet', to: { path: '/app/huddle' }, rule: 'can:huddle' },
  { id: 'tx', label: 'Tx Presenter', shortLabel: 'Tx Presenter', icon: 'present', description: 'Walk patients through treatment plans', to: { path: '/app/tx-presenter' }, rule: 'flag:canAccessTxPresenter' },
  { id: 'insurance', label: 'Insurance verification', shortLabel: 'Insurance', icon: 'shield', description: 'Eligibility checks before visits', to: { path: '/app/insurance-verification' }, rule: 'flag:showInsuranceVerificationNav' },
  { id: 'reviews', label: 'Reviews', shortLabel: 'Reviews', icon: 'star', description: 'Review requests and replies', to: { path: '/app/reviews' }, rule: 'can:reviews' },
  { id: 'insights', label: 'Team Insights', shortLabel: 'Insights', icon: 'chart', description: 'Production by provider and procedure', to: { path: '/app/team-insights' }, rule: 'can:team-insights' },
  { id: 'tasks', label: 'Tasks', shortLabel: 'Tasks', icon: 'checkSq', description: 'Your tasks and your team\u2019s', to: { path: '/app/todo' }, rule: 'can:tasks', countKey: 'tasks' },
  { id: 'kanban', label: 'Kanban', shortLabel: 'Kanban', icon: 'kanban', description: 'Boards for office projects', to: { path: '/app/kanban' }, rule: 'can:kanban', dot: 'kanban' },
  { id: 'chat', label: 'Chat', shortLabel: 'Chat', icon: 'chat', description: 'Message your team', to: { path: '/app/chat' }, rule: 'can:chat', dot: 'chat' },
  { id: 'sms', label: 'SMS conversations', shortLabel: 'Texts', icon: 'mobile', description: 'Two-way texting with patients', to: { path: '/app/conversations' }, rule: 'can:conversations', countKey: 'sms' },
  { id: 'wiki', label: 'Wiki', shortLabel: 'Wiki', icon: 'book', description: 'Office how-tos and policies', to: { path: '/app/wiki' }, rule: 'can:wiki' },
  { id: 'workflows', label: 'Workflow builder', shortLabel: 'Workflows', icon: 'workflow', description: 'Automations that run on their own', to: { path: '/app/workflow2-list' }, rule: 'can:workflow' },
  { id: 'forms', label: 'Forms', shortLabel: 'Forms', icon: 'form', description: 'Patient forms and submissions', to: { path: '/app/forms' }, rule: 'can:forms' },
  { id: 'online', label: 'Online appointments', shortLabel: 'Booking', icon: 'globe', description: 'Appointments booked from your website', to: { path: '/app/online-appointments' }, rule: 'can:online-appointments' },
  { id: 'campaigns', label: 'Campaigns', shortLabel: 'Campaigns', icon: 'megaphone', description: 'Recall and marketing campaigns', to: { path: '/app/campaigns' }, rule: 'can:campaigns' },
];

/** The classic sidebar's external help link — a fixed bottom row, not part of the arrangement. */
export const HELP_LINK = { label: 'Teamio how-to', href: 'https://teamio.app/help', icon: 'help', external: true };

/**
 * The four follow-up lists. key / subject / route.name MUST equal pageChrome.FOLLOWUP_TABS (copied
 * so this file stays import-free; the registry test fails if they drift). `path` is the route's
 * full path (src/Routes.js, children of /app); `icon` is the design's LISTS icon.
 */
export const FOLLOWUP_SUBJECTS = [
  { key: 'overdueRecall', subject: 'overdue_recall', route: { name: 'OverdueRecall' }, path: '/app/overdue-recall', icon: 'clock' },
  { key: 'unscheduledTreatment', subject: 'unscheduled_treatment', route: { name: 'unscheduledTreatment' }, path: '/app/unscheduled-treatment', icon: 'list' },
  { key: 'aging', subject: 'outstanding_claim', route: { name: 'Aging' }, path: '/app/aging', icon: 'dollar' },
  { key: 'brokenAppointments', subject: 'broken_appointment', route: { name: 'BrokenAppointments' }, path: '/app/broken-appointments', icon: 'calx' },
];

/** Paths that belong to the Follow-ups app: the four lists plus the two saved-list pages. */
export const FOLLOWUP_PATHS = FOLLOWUP_SUBJECTS.map(f => f.path).concat(['/app/overdue-recall-list', '/app/unscheduled-list']);

/**
 * Extra search words per page (design ALIAS). Keyed by registry id, by follow-up list key, and
 * 'myAccount' / 'settings' for the two account pages.
 */
export const PAGE_ALIASES = {
  tx: 'treatment plans',
  sms: 'texts texting',
  insurance: 'eligibility',
  online: 'booking',
  followups: 'lists recall',
  pulse: 'dashboard production pulse',
  reminders: 'confirmations',
  unscheduledTreatment: 'unscheduled tx',
  aging: 'claims',
  brokenAppointments: 'broken',
  myAccount: 'credits',
};

function followupAllowed(can, f) {
  return !!(can('view', f.subject) || can('view', 'all'));
}

/** The first follow-up list this user may open ({ name }), or null. */
export function firstFollowupRoute(can) {
  if (typeof can !== 'function') return null;
  const f = FOLLOWUP_SUBJECTS.find(x => followupAllowed(can, x));
  return f ? { name: f.route.name } : null;
}

/** Whether one registry row passes. Unknown rule kinds never pass. */
export function appAllowed(app, { can, flags } = {}) {
  const rule = app && typeof app.rule === 'string' ? app.rule : '';
  const canFn = typeof can === 'function' ? can : () => false;
  const f = flags || {};
  if (rule === 'always') return true;
  if (rule === 'followups') return firstFollowupRoute(canFn) !== null;
  if (rule.indexOf('can:') === 0) return !!canFn('view', rule.slice(4));
  if (rule.indexOf('flag:') === 0) return f[rule.slice(5)] === true;
  return false;
}

/**
 * The rows this user may see, in the given order — new objects (inputs are never mutated). The
 * Follow-ups row gets `to` = the first visible list.
 * @param {Array}    apps   registry rows (NAV_APPS)
 * @param {Function} can    (action, subject) → boolean — the app's $can
 * @param {Object}   flags  { isSuperAdmin, canAccessTxPresenter, showInsuranceVerificationNav }
 */
export function visibleApps(apps, { can, flags } = {}) {
  const ctx = { can, flags };
  return (apps || []).filter(a => appAllowed(a, ctx)).map((a) => {
    const copy = Object.assign({}, a);
    if (a.rule === 'followups') copy.to = firstFollowupRoute(can);
    return copy;
  });
}

function normalizePath(p) {
  let s = typeof p === 'string' ? p : '';
  const cut = s.search(/[?#]/);
  if (cut > -1) s = s.slice(0, cut);
  s = s.toLowerCase();
  while (s.length > 1 && s.charAt(s.length - 1) === '/') s = s.slice(0, -1);
  return s;
}

function ownsPath(prefix, path) {
  return path === prefix || path.indexOf(`${prefix}/`) === 0;
}

/**
 * The id of the app a route path belongs to, or null (patient profile, settings, …). Longest
 * prefix wins, on whole path segments ('/app/forms' owns '/app/forms/create-form', not
 * '/app/forms-x'). Follow-up list paths belong to 'followups'. Only ids present in `apps` count.
 */
export function activeAppId(apps, path) {
  const p = normalizePath(path);
  if (!p) return null;
  let best = null;
  let bestLen = -1;
  (apps || []).forEach((a) => {
    if (!a || !a.id) return;
    const prefixes = a.rule === 'followups' || a.id === 'followups'
      ? FOLLOWUP_PATHS
      : (a.to && typeof a.to.path === 'string' ? [a.to.path] : []);
    prefixes.forEach((raw) => {
      const pre = normalizePath(raw);
      if (pre && ownsPath(pre, p) && pre.length > bestLen) {
        best = a.id;
        bestLen = pre.length;
      }
    });
  });
  return best;
}
